import React from 'react';

import { getUserModifications } from 'components/product-detail-v2/product-customization/utils';
import { MenuObjectTypes } from 'enums/menu';
import {
  IProductWizardContext,
  IProductWizardFlowSelections,
  IProductWizardFlowSelectionsBase,
  IProductWizardFlowStep,
  IProductWizardProvider,
  IUserManualModifications,
  IUserSelection,
  Product,
  ProductMenuObject,
  SingleOrMultipleModifierSelection,
  SubmitCustomizationsFn,
  UserSelectionComboSlot,
} from 'state/product-wizard-v2/types';
import {
  ProductWizardUtils,
  getSingleOrMultipleModifierSelectionsDefaults,
} from 'state/product-wizard-v2/utils';
import { MIN_CART_QUANTITY } from 'utils/cart';
import noop from 'utils/noop';

import { useEditingCartEntry } from './hooks/use-editing-cart-entry';

const ProductWizardContext = React.createContext<IProductWizardContext>({
  // User selections
  comboSlotUpdate: [{ id: '', isDirty: false }],
  pickerAspectsUpdate: [{ id: '', isDirty: false }],
  itemModifiersUpdate: [{ id: '', isDirty: false }],
  productWizardFlowUserSelections: [{ id: '', isDirty: false, type: '' as MenuObjectTypes }],
  userSelections: {} as IUserSelection,

  // Flow steps
  productWizardFlowSteps: [],
  updateProductWizardFlowSteps: () => null,
  showCloseDialog: false,
  handleCloseDialog: () => undefined,
  pickerFlowHasUpdates: false,
  resetPickerFlowUpdates: () => undefined,

  // Update functions
  setProductQuantity: noop,
  setSingleOrMultipleModifierSelection: () => undefined,
  submitCustomizations: () => null,
  updatePickerAspectSelections: () => null,
  updateSelectionStateWithId: () => null,
  resetProductWizard: () => undefined,
  isBackNavigation: false,
  setIsBackNavigation: () => undefined,

  // Other variables
  isDirty: false,
  manualModifications: {} as IUserManualModifications,
  menuObject: {} as ProductMenuObject,
  productQuantity: MIN_CART_QUANTITY,
  selectedProduct: {} as Product,
  singleOrMultipleModifierSelection: null,
  updateSingleOrMultipleModifierSelection: () => undefined,
});

export const ProductWizardProvider: React.FC<React.PropsWithChildren<IProductWizardProvider>> = ({
  product: menuObject,
  children,
}) => {
  const editingCartEntry = useEditingCartEntry();
  const [manualModifications, setManualModifications] = React.useState<IUserManualModifications>({
    pickerAspects: false,
    comboSlot: false,
    modifiers: false,
  });

  const defaultSelections = { menuObject, cartEntryOverrides: editingCartEntry };

  const [userSelections, setUserSelections] = React.useState<IUserSelection>(() =>
    ProductWizardUtils.SetDefaultSelections(defaultSelections)
  );
  const [selectedProduct, setSelectedProduct] = React.useState<Product>(() =>
    ProductWizardUtils.ComputeSelectedOption(menuObject, userSelections.pickerAspects)
  );

  // @TODO: Add docs here
  const [productWizardFlowUserSelections, setProductWizardFlowUserSelections] = React.useState<
    IProductWizardFlowSelections[]
  >([]);

  // Tracks the whole complete step for each flow.
  const [productWizardFlowSteps, setProductWizardFlowSteps] = React.useState<
    IProductWizardFlowStep[]
  >([]);

  const [isBackNavigation, setIsBackNavigation] = React.useState<boolean>(false);

  const [showCloseDialog, handleCloseDialog] = React.useState<boolean>(false);

  // @TODO: Add docs here
  const updateProductWizardFlowSteps = React.useCallback((params: IProductWizardFlowStep) => {
    setProductWizardFlowSteps(prevUpdates => {
      // First, find the current step in `prevUpdates` and mark it as the previous step
      const updatedState = prevUpdates.map(step =>
        step.currentStep
          ? { ...step, currentStep: false, isPreviousStep: true }
          : { ...step, isPreviousStep: false }
      );

      // Find the index of the new current step in the updated state
      const existingIndex = updatedState.findIndex(state => state.id === params.id);

      // Set the new `currentStep` and reset `isPreviousStep` for the new current step
      if (existingIndex !== -1) {
        return updatedState.map((state, index) =>
          index === existingIndex ? { ...params, currentStep: true, isPreviousStep: false } : state
        );
      }

      // If the step does not exist, add it to the end and set it as the current step
      return [...updatedState, { ...params, currentStep: true, isPreviousStep: false }];
    });
  }, []);

  // @TODO: Add docs here
  const updateSelectionStateWithId = ({
    id,
    isDirty,
    isSaved = false,
    type,
  }: IProductWizardFlowSelections): void => {
    setProductWizardFlowUserSelections(prevState => {
      const existingItem = prevState.find(item => item.id === id);
      if (existingItem) {
        return prevState.map(item => (item.id === id ? { ...item, isDirty, isSaved } : item));
      }
      return [...prevState, { id, type, isDirty, isSaved }];
    });
  };

  const pickerAspectsUpdate: IProductWizardFlowSelectionsBase[] = productWizardFlowUserSelections.filter(
    ({ type }) => type === MenuObjectTypes.PICKER_ASPECT
  );

  const comboSlotUpdate: IProductWizardFlowSelectionsBase[] = productWizardFlowUserSelections.filter(
    ({ type }) => type === MenuObjectTypes.COMBO_SLOT
  );

  const itemModifiersUpdate: IProductWizardFlowSelectionsBase[] = productWizardFlowUserSelections.filter(
    ({ type }) => type === MenuObjectTypes.ITEM
  );

  const pickerFlowHasUpdates = React.useMemo(() => {
    const hasModifierSelections = !!itemModifiersUpdate.some(item => !!item.isDirty);
    const hasComboSlotUpdates = !!comboSlotUpdate.some(comboSlot => !!comboSlot.isDirty);

    return hasModifierSelections || hasComboSlotUpdates;
  }, [comboSlotUpdate, itemModifiersUpdate]);

  const resetPickerFlowUpdates = React.useCallback(() => {
    const updatedFlowSteps = productWizardFlowSteps.filter(
      flowStep => flowStep.type === MenuObjectTypes.PICKER_ASPECT
    );
    const updatedSelections = productWizardFlowUserSelections.reduce(
      (
        updatedSelections: IProductWizardFlowSelections[],
        selection: IProductWizardFlowSelections
      ) => {
        if (
          selection.type === MenuObjectTypes.COMBO_SLOT ||
          selection.type === MenuObjectTypes.ITEM
        ) {
          updatedSelections.push({
            ...selection,
            isDirty: false,
          });
        } else {
          updatedSelections.push(selection);
        }
        return updatedSelections;
      },
      []
    );

    // Resets defaults and user customizations.
    setProductWizardFlowUserSelections(updatedSelections);

    // Resets the product wizard steps, we could end having a different selectedProduct type ( item vs combo ),
    // hence a complete different flow with different steps.
    setProductWizardFlowSteps(updatedFlowSteps);

    // Resets Single or Multiple Choice user customizations.
    setSingleOrMultipleModifierSelection(null);
  }, [productWizardFlowSteps, productWizardFlowUserSelections]);

  const [
    singleOrMultipleModifierSelection,
    setSingleOrMultipleModifierSelection,
  ] = React.useState<SingleOrMultipleModifierSelection | null>(() =>
    getSingleOrMultipleModifierSelectionsDefaults(userSelections.comboSlot)
  );

  const updateSingleOrMultipleModifierSelection = React.useCallback(
    (slotKey: string, updateProps: Partial<SingleOrMultipleModifierSelection[string]>) => {
      // @ts-ignore prevState
      setSingleOrMultipleModifierSelection(prevState => {
        const prevModifierState = prevState?.[slotKey] || {};
        return {
          ...prevState,
          [slotKey]: {
            ...prevModifierState,
            ...updateProps,
          },
        };
      });
    },
    []
  );

  const defaultComboSlotSelections = React.useMemo<UserSelectionComboSlot>(
    () =>
      ProductWizardUtils.SetDefaultSelections({
        menuObject,
        pickerSelectionOverrides: userSelections.pickerAspects,
      }).comboSlot || new Map(),
    [menuObject, userSelections.pickerAspects]
  );

  React.useEffect(() => {
    if (!userSelections.comboSlot) {
      return;
    }
    const modifications = getUserModifications(
      userSelections.comboSlot,
      userSelections.modifiers,
      defaultComboSlotSelections
    );
    setManualModifications(prev => ({
      ...prev,
      comboSlot: prev.comboSlot || modifications.comboSlot,
      modifiers: prev.modifiers || modifications.modifiers,
    }));
  }, [defaultComboSlotSelections, userSelections.comboSlot, userSelections.modifiers]);

  const isDirty = React.useMemo<boolean>(() => {
    if (!userSelections.comboSlot) {
      return false;
    }
    const modifications = getUserModifications(
      userSelections.comboSlot,
      userSelections.modifiers,
      defaultComboSlotSelections
    );
    return modifications.comboSlot || modifications.modifiers;
  }, [defaultComboSlotSelections, userSelections.comboSlot, userSelections.modifiers]);

  const [productQuantity, setProductQuantity] = React.useState(
    editingCartEntry?.quantity || MIN_CART_QUANTITY
  );

  const updatePickerAspectSelections = React.useCallback(
    (args: any) => {
      setSelectedProduct(ProductWizardUtils.ComputeSelectedOption(menuObject, args));
      setUserSelections(
        ProductWizardUtils.SetDefaultSelections({
          menuObject,
          pickerSelectionOverrides: args,
        })
      );
      setManualModifications(prev => ({ ...prev, pickerAspects: true }));
    },
    [menuObject]
  );

  const submitCustomizations = React.useCallback<SubmitCustomizationsFn>(
    ({ modifiers, selectedItem, slotKey }) => {
      setUserSelections(current => {
        const comboSlot = current.comboSlot ? new Map(current.comboSlot) : new Map();
        comboSlot.set(slotKey, {
          comboSlot: current.comboSlot?.get(slotKey)?.comboSlot,
          selectedItem,
        });

        return {
          ...current,
          comboSlot,
          modifiers: {
            ...current.modifiers,
            [slotKey]: modifiers,
          },
        };
      });
    },
    []
  );

  const resetProductWizard = () => {
    ProductWizardUtils.SetDefaultSelections(defaultSelections);
    setProductWizardFlowSteps([]);
    setProductWizardFlowUserSelections([]);
    setSingleOrMultipleModifierSelection(null);
  };

  if (!selectedProduct) {
    return null;
  }

  return (
    <ProductWizardContext.Provider
      value={{
        // User selections
        comboSlotUpdate,
        pickerAspectsUpdate,
        itemModifiersUpdate,
        productWizardFlowUserSelections,
        userSelections,

        // Update functions
        setProductQuantity,
        setSingleOrMultipleModifierSelection,
        singleOrMultipleModifierSelection,
        submitCustomizations,
        updatePickerAspectSelections,
        updateSelectionStateWithId,
        resetProductWizard,
        isBackNavigation,
        setIsBackNavigation,

        // Flow steps
        productWizardFlowSteps,
        updateProductWizardFlowSteps,
        updateSingleOrMultipleModifierSelection,
        showCloseDialog,
        handleCloseDialog,
        pickerFlowHasUpdates,
        resetPickerFlowUpdates,

        // Other variables
        isDirty,
        manualModifications,
        menuObject,
        productQuantity,
        selectedProduct,
      }}
    >
      {children}
    </ProductWizardContext.Provider>
  );
};

export const useProductWizardContext = () => React.useContext(ProductWizardContext);
